import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  FlatList,
  ActivityIndicator,
} from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { SearchBar, AvatarCircle } from '@/components';
import { IConversation, IConversationAccount } from '@/types/admin/messages';
import { IApiUser } from '@/types/admin/userManagement';
import { useAssignAdminsMutation } from '@/store/api/admin/messagesApi';
import { useLazyGetUserRolesQuery } from '@/store/api/admin/userManagementApi';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useAppSelector } from '@/store';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';

interface ThreadOwnerModalProps {
  visible: boolean;
  onClose: () => void;
  conversation: IConversation;
}

export const ThreadOwnerModal: React.FC<ThreadOwnerModalProps> = ({
  visible,
  onClose,
  conversation,
}) => {
  const { layoutMode } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState<number[]>([]);
  
  const [getUserRoles, { data: usersData, isFetching }] = useLazyGetUserRolesQuery();
  const [assignAdmins, { isLoading: isAssigning }] = useAssignAdminsMutation();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const propertyId = useAppSelector((state) => state.adminUser.selectedPropertyId);

  // Get current assignees from conversation
  const currentAssignees = conversation.conversationAccounts
    ?.filter((acct: IConversationAccount) => acct.assignedAt !== null)
    .map((acct: IConversationAccount) => acct.assigneeAccount.id) || [];

  // Initialize selected assignees with current ones
  useEffect(() => {
    setSelectedAssignees(currentAssignees);
  }, [conversation]);

  // Fetch admin users when opened
  useEffect(() => {
    if (visible && propertyId) {
      getUserRoles(undefined);
    }
  }, [visible, propertyId, getUserRoles]);

  // Filter users based on search query
  const filteredUsers = usersData?.response?.filter((user: IApiUser) => {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
    const email = user.email.toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || email.includes(query);
  }) || [];

  // Toggle assignee selection
  const toggleAssignee = useCallback((userId: number) => {
    setSelectedAssignees(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  }, []);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const formData = new FormData();
      formData.append('conversationId', conversation.id.toString());
      formData.append('assignees', JSON.stringify(selectedAssignees));

      await assignAdmins(formData).unwrap();
      showSuccess('Thread owners updated successfully');
      onClose();
    } catch (error: any) {
      console.error('Assign admins error:', error);
      showApiError(error?.data?.message || 'Failed to update thread owners');
    }
  };

  const Body = (
    <View style={styles.content}>    
      {/* Instructions */}
      <View style={styles.instructionsContainer}>
        <Text style={styles.instructionsText}>
          Please assign the thread to others only if you do not have the necessary information.
        </Text>
      </View>

      {/* Search */}
      <SearchBar
        value={searchQuery}
        onChangeText={setSearchQuery}
        placeholder="Search admin users..."
        style={styles.searchBar}
      />

      {/* Users List */}
      <View style={styles.listContainer}>
        {isFetching ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={ADMIN_COLORS.PRIMARY} />
            <Text style={styles.loadingText}>Loading admin users...</Text>
          </View>
        ) : filteredUsers.length === 0 ? (
          <View style={styles.emptyContainer}>
            <SVG_COMPONENTS.GROUP
              width={48}
              height={48}
              color={ADMIN_COLORS.TEXT_SECONDARY}
            />
            <Text style={styles.emptyText}>
              {searchQuery ? `No users found for "${searchQuery}"` : 'No admin users available'}
            </Text>
          </View>
        ) : (
          <FlatList
            data={filteredUsers}
            renderItem={({ item }) => {
              const isSelected = selectedAssignees.includes(item.id);
              const roleName = item.roles?.[0]?.name || 'Admin';
              return (
                <TouchableOpacity style={styles.userItem} onPress={() => toggleAssignee(item.id)}>
                  <AvatarCircle firstName={item.firstName || ''} lastName={item.lastName || ''} size={40} style={styles.avatar} />
                  <View style={styles.userInfo}>
                    <Text style={styles.userName}>{item.firstName || ''} {item.lastName || ''}</Text>
                    <Text style={styles.userRole}>{roleName}</Text>
                  </View>
                  <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                    {isSelected && (<SVG_COMPONENTS.CHECK_CIRCLE width={16} height={16} color="#FFFFFF" />)}
                  </View>
                </TouchableOpacity>
              );
            }}
            keyExtractor={(item) => item.id.toString()}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.listContent}
          />
        )}
      </View>
    </View>
  );

  if (layoutMode === 'desktop') {
    return (
      <DesktopSidePanel visible={visible} onClose={onClose} width={520}>
        <DesktopPanelHeader title="Thread Owners" onClose={onClose} right={(
          <TouchableOpacity onPress={handleSubmit} disabled={isAssigning} style={{ paddingHorizontal: 12, paddingVertical: 6 }}>
            {isAssigning ? (
              <ActivityIndicator size="small" color={ADMIN_COLORS.PRIMARY} />
            ) : (
              <Text style={{ color: ADMIN_COLORS.PRIMARY, fontWeight: '600' }}>Save</Text>
            )}
          </TouchableOpacity>
        )} />
        {Body}
      </DesktopSidePanel>
    );
  }

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <SVG_COMPONENTS.CHEVRON_LEFT
              width={24}
              height={24}
              color={ADMIN_COLORS.TEXT.PRIMARY}
            />
          </TouchableOpacity>
          
          <Text style={styles.headerTitle}>Thread Owners</Text>
          
          <TouchableOpacity
            style={styles.saveButton}
            onPress={handleSubmit}
            disabled={isAssigning}
          >
            {isAssigning ? (
              <ActivityIndicator size="small" color={ADMIN_COLORS.PRIMARY} />
            ) : (
              <Text style={styles.saveButtonText}>Save</Text>
            )}
          </TouchableOpacity>
        </View>

        {/* Content */}
        {Body}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    paddingTop: 20,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY, 
    textAlign: 'center',
  },
  saveButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginLeft: 8,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.PRIMARY,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  instructionsContainer: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    marginTop: 16,
  },
  instructionsText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    lineHeight: 20,
    textAlign: 'center',
  },
  searchBar: {
    marginVertical: 16,
  },
  listContainer: {
    flex: 1,
  },
  listContent: {
    paddingBottom: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginTop: 16,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    marginTop: 16,
    lineHeight: 22,
  },
  userItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    padding: 16,
    marginBottom: 8,
  },
  avatar: {
    marginRight: 12,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  userRole: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
});
